// Utilities for URL's and ItemBreadcrumbs
export const siteUrl = 'https://www.taskpogo.com';

// URL for unsubscribes. TODO: Should the key/itemId be posted?
export function urlForUnsubscribe(itemId, unsubscribeKey) {
    return `${siteUrl}/unsubscribe?itemId=${encodeURIComponent(itemId)}&unsubscribeKey=${encodeURIComponent(unsubscribeKey)}`;
}

// Convenience alias for callers (mostly email Lambdas) that want the absolute
// unsubscribe URL but read naturally with the other `fullUrlFor*` helpers.
export function fullUrlForUnsubscribe(itemId, unsubscribeKey) {
    return urlForUnsubscribe(itemId, unsubscribeKey);
}

export function urlForSupport() {
    return '/support';
}

export function isUrlForSupport(url) {
    return (url.toLowerCase() === "/support");
}

export function urlForSalish() {
    return '/salish';
}

export function fullUrlForSalish() {
    return `${siteUrl}${urlForSalish()}`;
}

export function urlForPrivacy() {
    return '/privacy';
}

export function urlForTerms() {
    return '/terms';
}

export function urlForProfile() {
    return '/me/profile';
}

export function urlForChangePassword() {
    return '/me/changepass';
}

export function urlForAllItems() {
    return '/me/item';
}

export function isUrlForAllItems(url) {
    return (url.toLowerCase() === '/me/item');
}

export function isUrlUnderAllItems(url) {
    return url.startsWith('/me/item');
}

export function urlForItem(itemId) {
    return `/me/item/${itemId}`;
}

export function fullUrlForItem(itemId) {
    return `${siteUrl}${urlForItem(itemId)}`;
}

export function urlForItemImage(itemId) {
    return `/me/item/${itemId}/image`;    
}

export function urlForEmailItem(itemId) {
    return `/me/item/${itemId}/email`;
}

export function fullUrlForEmailItem(itemId) {
    return `${siteUrl}${urlForEmailItem(itemId)}`
}

export function urlForTaskId(itemId, taskId) {
    return `/me/item/${itemId}/task/${taskId}`
}

export function fullUrlForTaskId(itemId, taskId) {
    return `${siteUrl}${urlForTaskId(itemId, taskId)}`;
}

export function urlForEditTaskId(itemId, taskId) {
    return `/me/item/${itemId}/task/${taskId}?edit=1`
}

// Deep-link to the item page that opens the Edit Hours modal for the given
// counter. Counters have no standalone page; the item page reads `?counter=`
// on mount and pops the edit UI.
export function urlForItemCounter(itemId, counterId) {
    return `/me/item/${itemId}?counter=${encodeURIComponent(counterId)}`;
}

export function fullUrlForItemCounter(itemId, counterId) {
    return `${siteUrl}${urlForItemCounter(itemId, counterId)}`;
}

export const bucketNameAll = 'all';
export const bucketNameOverdue = 'overdue';
export const bucketNameSoon = 'soon';

export function urlForTasks(itemId, bucketName) {
    return `/me/item/${itemId}/tasks/${bucketName}`
}

export function fullUrlForTasks(itemId, bucketName = bucketNameAll) {
    return `${siteUrl}${urlForTasks(itemId, bucketName)}`;
}

// Every open task on one item assigned to exactly `assignee` (the free-text
// TaskPogoTask.assignedEmail string, matched case-sensitively).
//
// The name rides in the QUERY STRING, not a path segment, for two reasons that
// are each sufficient. React Router decodes a segment and then re-escapes any
// '/' it produced, so a name like "a/b" reaches useParams as "a%2Fb" and needs
// a second, guarded decode. And Amplify Hosting's SPA rewrite skips paths whose
// last dotted component looks like a static asset (js, json, png, ...), so
// ".../assigned/bob.js" would 404 at the CDN before the app ever loaded.
// `?to=` round-trips every character through URLSearchParams. The static
// `tasks/assigned` segment outranks the `tasks/:listType` route beside it.
export const tasksAssignedSegment = 'assigned';

export function urlForTasksAssignedTo(itemId, assignee) {
    return `/me/item/${itemId}/tasks/${tasksAssignedSegment}?to=${encodeURIComponent(assignee)}`;
}

// The same list as an absolute URL, for the owner's notification mail: a link in
// an email has no app to be relative to. Same pair as fullUrlForTasks above.
export function fullUrlForTasksAssignedTo(itemId, assignee) {
    return `${siteUrl}${urlForTasksAssignedTo(itemId, assignee)}`;
}

/**
 * When opening a task from `/me/item/:id/tasks/:listType`, React Router location
 * state may include `tasksListBucket` so the task detail page can return to that
 * list after delete (or link the Tasks breadcrumb to the same bucket).
 */
export function tasksListSegmentFromNavigationState(state) {
    if (!state || typeof state.tasksListBucket !== 'string') {
        return null;
    }
    const b = state.tasksListBucket;
    if (b === bucketNameAll || b === bucketNameOverdue || b === bucketNameSoon) {
        return b;
    }
    return null;
}

// The cross-item Trips page (top-nav "Trips"). Groups every item that has trip
// content -- a Captain's/Pilot's Log or any trip -- under its own heading. The
// per-item pages below are still where the actual editing happens.
export function urlForAllTrips() {
    return '/me/trips';
}

// The gazetteer editor: every stored TaskPogoPlace on one map. Not item-scoped
// -- the place database is shared by every user and every trip, which is why it
// hangs off /me rather than /me/item/:itemId.
export function urlForPlaces() {
    return '/me/places';
}

// One place's edit page.
export function urlForPlace(placeId) {
    return `/me/places/${placeId}`;
}

// The full upcoming-trips list for an item.
export function urlForTrips(itemId) {
    return `/me/item/${itemId}/trips`;
}

export function fullUrlForTrips(itemId) {
    return `${siteUrl}${urlForTrips(itemId)}`;
}

// The page for a single grouped trip (all legs sharing a Trip Name, or one
// unnamed leg). `groupKey` is a TripGroups key ('n:<name>' / 't:<id>'); encode
// it so names containing slashes or other reserved characters stay path-safe.
export function urlForTripGroup(itemId, groupKey) {
    return `/me/item/${itemId}/trips/group/${encodeURIComponent(groupKey)}`;
}

// The same page, absolute -- for the trip weather email's "Open this trip"
// link, which is read outside the app and has no origin to be relative to.
export function fullUrlForTripGroup(itemId, groupKey) {
    return `${siteUrl}${urlForTripGroup(itemId, groupKey)}`;
}

// The detail page for a single trip.
export function urlForTrip(itemId, tripId) {
    return `/me/item/${itemId}/trip/${tripId}`;
}

// Legacy logbook landing. An Item can now have several logbooks, so this bare
// path no longer maps to a single list -- it renders a redirect that sends the
// user to the item's first logbook (or the item page when it has none). Status
// emails still link here via `fullUrlForLogbook`, so keep the shape stable.
export function urlForLogbook(itemId) {
    return `/me/item/${itemId}/logbook`;
}

export function fullUrlForLogbook(itemId) {
    return `${siteUrl}${urlForLogbook(itemId)}`;
}

// The list page for a specific logbook.
export function urlForLogbookForId(itemId, logbookId) {
    return `/me/item/${itemId}/logbook/${logbookId}`;
}

export function urlForLogbookEntry(itemId, logbookId, entryId) {
    return `/me/item/${itemId}/logbook/${logbookId}/${entryId}`;
}

// One trip's worth of entries inside a Captain's Log: every entry filed under
// the same trip, on one map. `tripKey` is an entryTripKey ('g:<trip id>', or a
// legacy 'n:<name>' from before trips had ids -- LogbookTypes.entryTripKey),
// the same shape urlForTripGroup carries and encoded for the same reason: a
// legacy key holds a hand-typed name, which may contain slashes or other
// reserved characters.
export function urlForLogbookTrip(itemId, logbookId, tripKey) {
    return `/me/item/${itemId}/logbook/${logbookId}/trip/${encodeURIComponent(tripKey)}`;
}

// The same, absolute. Mail needs one: TaskPogoShareUpload tells the owner that a
// visitor added photos, and a relative path in an email goes nowhere. Here rather
// than concatenated in the handler for the reason every other fullUrlFor* exists
// -- one spelling of the site, and a rename of the route moves both.
export function fullUrlForLogbookTrip(itemId, logbookId, tripKey) {
    return `${siteUrl}${urlForLogbookTrip(itemId, logbookId, tripKey)}`;
}

// One year's worth of a Captain's Log: every trip logged that year, on one
// map. Unlike a trip name, a year is digits -- nothing to encode.
export function urlForLogbookYear(itemId, logbookId, year) {
    return `/me/item/${itemId}/logbook/${logbookId}/year/${year}`;
}

// Everything logged at one gazetteer place: every trip leg and logbook entry
// whose route (or From/To) reaches it, on one map.
export function urlForPlaceVisits(placeId) {
    return `/me/places/${placeId}/visits`;
}

export function urlForLogbookPickTask(itemId, logbookId) {
    return `/me/item/${itemId}/logbook/${logbookId}/picktask`;
}

export function urlForLogbookEntryFromTask(itemId, logbookId, taskId) {
    return `/me/item/${itemId}/logbook/${logbookId}/fromtask/${taskId}`;
}

export function urlForSharedTasks(shareId) {
    return `/me/shared/${shareId}`;
}

export function fullUrlForSharedTasks(shareId) {
    return `${siteUrl}/me/shared/${shareId}`;
}

// Public shared-trip view -- deliberately NOT under /me: anyone with the link
// can open it with no account.
export function urlForSharedTrip(shareId) {
    return `/shared/trip/${shareId}`;
}

export function fullUrlForSharedTrip(shareId) {
    return `${siteUrl}${urlForSharedTrip(shareId)}`;
}

export function urlForSharedTripLeg(shareId, legId) {
    return `/shared/trip/${shareId}/leg/${legId}`;
}

// Public shared LOGBOOK-trip view -- the Captain's Log counterpart of the
// three above, and out here beside them for the same reason.
export function urlForSharedLogbookTrip(shareId) {
    return `/shared/logbook/${shareId}`;
}

export function fullUrlForSharedLogbookTrip(shareId) {
    return `${siteUrl}${urlForSharedLogbookTrip(shareId)}`;
}

export function urlForSharedLogbookEntry(shareId, entryId) {
    return `/shared/logbook/${shareId}/entry/${entryId}`;
}

// Public shared view of one item's tasks assigned to one person -- the third
// of the /shared pages, out here beside the others for the same reason.
export function urlForSharedAssignedTasks(shareId) {
    return `/shared/tasks/${shareId}`;
}

export function fullUrlForSharedAssignedTasks(shareId) {
    return `${siteUrl}${urlForSharedAssignedTasks(shareId)}`;
}


// S3 path for storing items for the given id.
export function urlForItemStorage(itemId) {
    return `${itemId}/`;
}


// ******* Thumbnails *************************
// All 'thumbnails' have this prefix:
export const thumbnailPrefix = '~~thumbnail-';
export const thumbnailSmallSuffix = 'small.jpg';
export const thumbnailMediumSuffix = 'medium.jpg';

// The "small" thumbnail has a max width/height of:
export const ThumbnailSmallSize = 64;
export const ThumbnailMediumSize = 256;

export function itemThumbnailPrefix(bucket) {
    return `${bucket}${thumbnailPrefix}`;
} 

// This is the URL used when originally uploading the thumbnail. A Lambda, TaskPogoImageProcessor
// then resizes to small/medium and renames the uploaded file to urlForItemThumbnailOriginal...
export function urlForItemThumbnailUpload(itemId, filename) {
  return `${itemThumbnailPrefix(urlForItemStorage(itemId))}${filename}`;
}

// This is the URL for the "small" thumbnail. The server resizes the original to this size:
export function urlForItemThumbnailSmall(itemId) {
  return `${itemThumbnailPrefix(urlForItemStorage(itemId))}${thumbnailSmallSuffix}`;
}

// This is the URL for the "medium" thumbnail. The server resizes the original to this size:
export function urlForItemThumbnailMedium(itemId) {
  return `${itemThumbnailPrefix(urlForItemStorage(itemId))}${thumbnailMediumSuffix}`;
}

// The key portion (no bucket or user/itemId path) for S3 for the small thumbnail. Used by server to create the resized original.
export function keyForThumbnailSmall() {
    return `${thumbnailPrefix}small.jpg`;
}

// The key portion (no bucket) for S3 for the medium thumbnail. Used by server to create the resized original.
export function keyForThumbnailMedium() {
    return `${thumbnailPrefix}medium.jpg`;
}


// ****************************************************
// The item-wide TASK prefix. Every task's attachments live under it, one folder
// per task id -- the same shape urlForLogbookStorage has, and for the same
// reason: one listing of this prefix carries every task's photos and the
// grouping is a path segment (groupPhotosByEntry, Attachments.mjs). That is
// what lets a task LIST show its rows' photos on one round trip.
//
// Not derivable as urlForTaskStorage(itemId, ''): that doubles the slash.
export function urlForTaskStorageRoot(itemId) {
    return `${itemId}/task/`;
}

// S3 path for storing items for the given item/taskId. Built over the root
// above so the lister and the writer cannot disagree about the prefix.
export function urlForTaskStorage(itemId, taskId) {
    return `${urlForTaskStorageRoot(itemId)}${taskId}/`;
}

// The item-wide logbook prefix. Every entry's attachments live under it, one
// folder per entry id -- note there is no logbookId in the path, so this is
// EVERY logbook's attachments for the item. Stated once so a reader that
// groups keys by entry cannot disagree with the writer that creates them.
// Not derivable as urlForLogbookEntryStorage(itemId, ''): that doubles the slash.
export function urlForLogbookStorage(itemId) {
    return `${itemId}/logbook/`;
}

export function urlForLogbookEntryStorage(itemId, entryId) {
    return `${urlForLogbookStorage(itemId)}${entryId}/`;
}


// ******* Attachment previews *********************
//
// A downscaled copy of an attachment photo, beside the original in the same
// folder. The originals are what the camera produced -- 4-6 MB and 12 megapixels
// is ordinary -- and every surface that shows one was downloading all of it to
// fill a box a few hundred pixels wide. Worse than slow: iOS Safari caps how
// much decoded image a page may hold, and a couple of 12 MP photos exceed it,
// so the <img> renders as nothing at all with no error to notice.
//
// DELIBERATELY NOT the `~~thumbnail-` prefix above. That one belongs to the
// item's profile photo, and image-processor's deleteOldThumbs sweeps every
// `~~thumbnail-*` key directly under an item's folder whenever a new profile
// photo is uploaded -- which would silently delete the previews of every
// attachment on the item itself. The two prefixes are disjoint: '~~thumbnail-'
// and '~~preview-' differ at index 7.
export const attachmentPreviewPrefix = '~~preview-';

// One size serves every surface. 512 px covers the largest box we draw (a
// ~240 px card thumbnail on a 2x screen) and costs ~40-80 KB against the
// original's several MB.
export const AttachmentPreviewSize = 512;

// The file name portion of a key, for a key with or without any folder.
function fileNameOfKey(key) {
    return key.slice(key.lastIndexOf('/') + 1);
}

export function isAttachmentPreviewKey(key) {
    return fileNameOfKey(key).startsWith(attachmentPreviewPrefix);
}

// The preview that belongs to an attachment, in the same folder. The original's
// FULL name is kept and `.jpg` appended -- previews are always re-encoded to
// JPEG, and stripping the extension instead would collide `a.png` with `a.jpeg`.
export function attachmentPreviewKey(key) {
    const cut = key.lastIndexOf('/') + 1;
    return `${key.slice(0, cut)}${attachmentPreviewPrefix}${key.slice(cut)}.jpg`;
}
